import axios from 'axios'
import type {
  SendCodeResponse,
  VerifyCodeResponse,
  GuestBookingsResponse,
  GuestOrdersResponse,
  GuestDashboardSummary
} from '@/types/guest'
import { getOriginHostForEmailCta } from '@/utils/originHost'

// Create a separate axios instance for guest API calls
// This allows us to handle guest auth separately from the main auth flow
const guestApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:50000/api',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
})

// Store reference to guest store for token access
let guestStoreRef: (() => any) | null = null

// Initialize guest store reference
export function initializeGuestService() {
  import('@/stores/guest').then(({ useGuestStore }) => {
    guestStoreRef = useGuestStore
    console.log('Guest service initialized')
  })
}

// Helper to get guest token
function getGuestToken(): string | null {
  // Try to get from store first
  if (guestStoreRef) {
    try {
      const store = guestStoreRef()
      if (store.guestToken) {
        return store.guestToken
      }
    } catch (error) {
      console.warn('Could not get guest store:', error)
    }
  }

  // Fallback: get directly from localStorage (for page refresh scenarios)
  try {
    const stored = localStorage.getItem('guest_session')
    if (stored) {
      const session = JSON.parse(stored)
      const expiresAt = new Date(session.expiresAt)
      // Only return token if not expired
      if (new Date() < expiresAt) {
        return session.token
      }
    }
  } catch (error) {
    console.warn('Could not get guest token from localStorage:', error)
  }

  return null
}

// Request interceptor for guest API
guestApi.interceptors.request.use(
  async (config) => {
    // Add guest token for protected endpoints
    if (config.url?.startsWith('/guest/')) {
      const token = getGuestToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Add CSRF token for state-changing requests
    const isStateChangingRequest = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')
    if (isStateChangingRequest && config.headers) {
      // Fetch CSRF token if needed
      try {
        const csrfResponse = await axios.get(`${import.meta.env.VITE_API_URL || 'http://localhost:50000/api'}/auth/csrf-token`, {
          withCredentials: true
        })
        if (csrfResponse.data.token) {
          config.headers['X-XSRF-TOKEN'] = csrfResponse.data.token
        }
      } catch (error) {
        console.warn('Failed to fetch CSRF token for guest request:', error)
        // Reject the request — without CSRF token, the server will reject it anyway
        // Better to fail fast with a clear message than let it through silently
        return Promise.reject(new Error('Unable to secure your request. Please refresh the page and try again.'))
      }
    }

    console.log(`Guest API Request: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error('Guest API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
guestApi.interceptors.response.use(
  (response) => {
    console.log(`Guest API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('Guest API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message
    })

    // Handle 401 - guest token expired
    if (error.response?.status === 401 && error.config?.url?.startsWith('/guest/')) {
      console.warn('Guest token expired')
      // Clear guest session via store
      if (guestStoreRef) {
        try {
          const store = guestStoreRef()
          store.clearGuestSession()
        } catch (e) {
          console.error('Failed to clear guest session:', e)
        }
      }
    }

    return Promise.reject(error)
  }
)

export const guestService = {
  /**
   * Send verification code to guest email. Includes the current page's
   * hostname so the backend can detect WL custom-domain traffic — the API
   * is hosted at api.zaveato.co.uk, so the server's own Host header is
   * useless for shop matching; the customer's hostname must come from the
   * browser explicitly.
   */
  async sendCode(email: string, purpose?: string): Promise<SendCodeResponse> {
    const response = await guestApi.post<SendCodeResponse>('/auth/guest/send-code', {
      email,
      purpose,
      originHostname: getOriginHostForEmailCta(),
    })
    return response.data
  },

  /**
   * Verify code and get guest access token
   */
  async verifyCode(email: string, code: string): Promise<VerifyCodeResponse> {
    const response = await guestApi.post<VerifyCodeResponse>('/auth/guest/verify-code', { email, code })
    return response.data
  },

  /**
   * Get all bookings for verified guest email
   */
  async getBookings(): Promise<GuestBookingsResponse> {
    const response = await guestApi.get<GuestBookingsResponse>('/guest/bookings')
    return response.data
  },

  /**
   * Get all orders for verified guest email
   */
  async getOrders(): Promise<GuestOrdersResponse> {
    const response = await guestApi.get<GuestOrdersResponse>('/guest/orders')
    return response.data
  },

  /**
   * Get dashboard summary for verified guest
   */
  async getDashboard(): Promise<GuestDashboardSummary> {
    const response = await guestApi.get<GuestDashboardSummary>('/guest/dashboard')
    return response.data
  }
}

export default guestService
